import React, { useEffect, useMemo, useState } from 'react';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { fetchHistoryRaw } from '../../services/dataApi';
import { formatInTZ } from '../../utils/timezone';
import { CHART_COLORS } from '../surveillance/surveillanceShared';
import { fetchSetpointDeviations, Deviation } from '../../services/setpointsApi';
import { formatDeviation } from '../../utils/deviation';

interface DeviceHistoryChartProps {
    deviceIeee: string;
    deviceName: string;
    property: string;
    propertyLabel: string;
    timezone?: string | null;
    onClose: () => void;
}

interface DataRow {
    ieee_address: string;
    property: string;
    value_num: number | null;
    value_str: string | null;
    parameter_name: string | null;
    parameter_unit: string | null;
    enum_values: { value: string; text: string }[] | null;
    timestamp: string;
}

const DeviceHistoryChart: React.FC<DeviceHistoryChartProps> = ({
    deviceIeee,
    deviceName,
    property,
    propertyLabel,
    timezone,
    onClose,
}) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [historyData, setHistoryData] = useState<DataRow[]>([]);
    const [deviation, setDeviation] = useState<Deviation | null>(null);
    const overlayRef = React.useRef<HTMLDivElement | null>(null);

    // Try to lock to landscape when chart opens (Android/Chrome only; iOS Safari blocks)
    useEffect(() => {
        const el = overlayRef.current;
        if (!el) return;

        let didRequestFullscreen = false;
        (async () => {
            try {
                if (el.requestFullscreen) {
                    await el.requestFullscreen();
                    didRequestFullscreen = true;
                } else if ((el as any).webkitRequestFullscreen) {
                    (el as any).webkitRequestFullscreen();
                    didRequestFullscreen = true;
                }
            } catch { /* user gesture missing or unsupported */ }

            try {
                const orientation = (window.screen as any)?.orientation;
                if (orientation?.lock) {
                    await orientation.lock('landscape');
                }
            } catch { /* iOS / desktop / locked by system — ignore */ }
        })();

        // Close overlay if user exits fullscreen via OS gesture (e.g. ESC, swipe)
        const onFsChange = () => {
            if (!document.fullscreenElement && !(document as any).webkitFullscreenElement) {
                onClose();
            }
        };
        document.addEventListener('fullscreenchange', onFsChange);
        document.addEventListener('webkitfullscreenchange', onFsChange);

        return () => {
            document.removeEventListener('fullscreenchange', onFsChange);
            document.removeEventListener('webkitfullscreenchange', onFsChange);
            try { (window.screen as any)?.orientation?.unlock?.(); } catch {}
            if (didRequestFullscreen) {
                try {
                    if (document.fullscreenElement && document.exitFullscreen) {
                        document.exitFullscreen();
                    } else if ((document as any).webkitFullscreenElement && (document as any).webkitExitFullscreen) {
                        (document as any).webkitExitFullscreen();
                    }
                } catch {}
            }
        };
    }, [onClose]);

    // Fetch last 24 hours of data
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const to = new Date();
                const from = new Date(to.getTime() - 24 * 60 * 60 * 1000);
                
                const formatISO = (d: Date) => {
                    const pad = (n: number) => String(n).padStart(2, '0');
                    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
                };

                setHistoryData(await fetchHistoryRaw<DataRow>({ ieees: deviceIeee, from: formatISO(from), to: formatISO(to), properties: property }, 30000));
            } catch (e: any) {
                const msg = e?.code === 'ECONNABORTED'
                    ? 'Det tog för lång tid att hämta'
                    : (e?.response?.data?.message ?? e?.message ?? 'Kunde inte hämta diagramdata');
                setError(msg);
                console.error('DeviceHistoryChart fetch error:', e);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [deviceIeee, property]);

    // Fetch the 24 h deviation against this property's setpoint (if any).
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const devs = await fetchSetpointDeviations([deviceIeee], 24);
                if (cancelled) return;
                setDeviation(devs.find(d => d.parameter_key === property) ?? null);
            } catch { if (!cancelled) setDeviation(null); }
        })();
        return () => { cancelled = true; };
    }, [deviceIeee, property]);

    // Build chart data
    const { chartData, displayMap, enumLabels, isEnum } = useMemo(() => {
        if (historyData.length === 0) {
            return { chartData: [], displayMap: {}, enumLabels: [], isEnum: false };
        }

        const pointMap = new Map<number, Record<string, any>>();
        const displayMap: Record<string, string> = {};
        let enumLabels: string[] = [];
        let isEnum = false;

        // Check if property is enum-based
        const firstRow = historyData[0];
        if (firstRow?.enum_values?.length) {
            isEnum = true;
            const enumLabelSet = new Set<string>();
            for (const row of historyData) {
                if (row.enum_values?.length) {
                    for (const e of row.enum_values) enumLabelSet.add(e.text);
                }
            }
            enumLabels = Array.from(enumLabelSet).sort();
        }

        const enumLabelIndex = new Map<string, number>();
        enumLabels.forEach((lbl, i) => enumLabelIndex.set(lbl, i));

        const seriesKey = `${deviceIeee}::${encodeURIComponent(property)}`;

        for (const row of historyData) {
            const ts = new Date(row.timestamp).getTime();
            if (isNaN(ts)) continue;

            if (!pointMap.has(ts)) {
                pointMap.set(ts, { ts });
            }

            const point = pointMap.get(ts)!;
            const displayKey = `${ts}::${seriesKey}`;

            if (isEnum && row.enum_values?.length) {
                // Enum value: map to index
                const numVal = row.value_num !== null ? Number(row.value_num) : null;
                const enumEntry = row.enum_values.find(e => Number(e.value) === numVal);
                const displayText = enumEntry?.text ?? String(numVal ?? row.value_str ?? '');
                displayMap[displayKey] = displayText;
                const idx = enumLabelIndex.get(displayText);
                point[seriesKey] = idx !== undefined ? idx : null;
            } else {
                // Numeric value
                const val = row.value_num !== null ? row.value_num : null;
                point[seriesKey] = val;

                if (row.value_str && row.value_str !== String(val)) {
                    displayMap[displayKey] = row.value_str;
                } else if (val !== null) {
                    displayMap[displayKey] = String(val);
                }
            }
        }

        const chartData = Array.from(pointMap.values()).sort((a, b) => a.ts - b.ts);
        return { chartData, displayMap, enumLabels, isEnum };
    }, [historyData, deviceIeee, property]);

    const seriesKey = `${deviceIeee}::${encodeURIComponent(property)}`;

    const renderChart = () => (
        <LineChart data={chartData} margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
                dataKey="ts"
                type="number"
                scale="time"
                domain={['dataMin', 'dataMax']}
                tickFormatter={ts => formatInTZ(ts as number, timezone, {
                    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                })}
                tick={{ fontSize: 11 }}
                minTickGap={60}
            />
            {isEnum ? (
                <YAxis
                    width={120}
                    tick={{ fontSize: 10 }}
                    domain={[-0.5, enumLabels.length - 0.5]}
                    ticks={enumLabels.map((_, i) => i)}
                    tickFormatter={(val: number) => enumLabels[val] ?? ''}
                    allowDecimals={false}
                />
            ) : (
                <YAxis width={72} tick={{ fontSize: 11 }} />
            )}
            <Tooltip
                labelFormatter={ts => formatInTZ(ts as number, timezone)}
                formatter={(value: any, name: string | undefined, props: any) => {
                    const ts = props?.payload?.ts;
                    const displayStr = ts ? displayMap[`${ts}::${name}`] : undefined;
                    if (isEnum && displayStr) {
                        return [displayStr, propertyLabel];
                    }
                    const formatted = displayStr && displayStr !== String(value)
                        ? `${displayStr} (${value})`
                        : typeof value === 'number'
                            ? value.toLocaleString(undefined, { maximumFractionDigits: 3 })
                            : String(value);
                    return [formatted, propertyLabel];
                }}
                contentStyle={{ fontSize: '12px' }}
            />
            <Line
                type="monotone"
                dataKey={seriesKey}
                stroke={CHART_COLORS[0]}
                dot={chartData.length < 20 ? { r: 3 } : false}
                strokeWidth={2}
                connectNulls={true}
            />
        </LineChart>
    );

    return (
        <div className="chart-fullscreen-overlay" ref={overlayRef}>
            <div className="chart-fullscreen-header">
                <span>📈 {deviceName} · {propertyLabel} (24h)</span>
                <button className="chart-fullscreen-close" onClick={onClose}>✕ Stäng</button>
            </div>
            <div className="chart-rotate-hint">
                <span>↻</span>
                <span>Vrid telefonen för ett bredare diagram</span>
            </div>
            <div className="chart-fullscreen-body">
                {loading ? (
                    <p style={{ color: '#888', textAlign: 'center', padding: '32px 0', margin: 0 }}>Hämtar…</p>
                ) : error ? (
                    <p style={{ color: '#c0392b', textAlign: 'center', padding: '32px 0', margin: 0 }}>{error}</p>
                ) : chartData.length === 0 ? (
                    <p style={{ color: '#888', textAlign: 'center', padding: '32px 0', margin: 0 }}>
                        Det finns inga värden från det senaste dygnet
                    </p>
                ) : (
                    <ResponsiveContainer width="100%" height="100%">
                        {renderChart()}
                    </ResponsiveContainer>
                )}
            </div>
            {/* Avvikelse mot börvärde + tidsrymd */}
            {!loading && !error && (
                <div style={{ padding: '8px 16px', fontSize: 12, color: '#444', borderTop: '1px solid #eee', background: '#fafafa' }}>
                    {deviation ? (
                        <>
                            <span>
                                Avvikelse: <strong>{formatDeviation(deviation.devAvg, deviation.devNull)}</strong> (snitt)
                                {' · '}börvärde {deviation.target_value}{deviation.unit ? ` ${deviation.unit}` : ''}
                            </span>
                            <div style={{ color: '#888', marginTop: 2 }}>
                                Period: {formatInTZ(new Date(deviation.from).getTime(), timezone)} – {formatInTZ(new Date(deviation.to).getTime(), timezone)} (24 h)
                            </div>
                        </>
                    ) : (
                        <span style={{ color: '#888' }}>Avvikelse: n/a (inget börvärde satt) · Period: senaste 24 h</span>
                    )}
                </div>
            )}
        </div>
    );
};

export default DeviceHistoryChart;
